import { Link } from 'react-router-dom';
import {
  Star,
  GraduationCap,
  Heart,
  Handshake,
  CalendarCheck,
  MapPinLine,
  Buildings,
  Users,
  Quotes,
  Scales,
} from '@phosphor-icons/react';
import { BRAND } from '../lib/brand';

export const AboutPage = () => {
  const credentials = [
    {
      icon: GraduationCap,
      title: "Master of Business Administration",
      description: "Trained in strategy, contract management, and large-scale project execution. The same rigor I learned in graduate school, I now bring to every signing — whether it's your kitchen table or a closing-day boardroom.",
    },
    {
      icon: Users,
      title: "17 Years · Program Director, Nashville Megachurch",
      description: "Served as Program Director managing children and youth ministries at a prominent 30,000-member Nashville congregation. Specialized in program development, family-experience design, and large-scale asset coordination across multi-site campuses.",
    },
    {
      icon: Buildings,
      title: "Trusted Partner Notary · National Homebuilders",
      description: "A trusted partner notary facilitating high-volume residential real-estate closings with Pulte and other top-tier national homebuilders. Clean documentation, deadline discipline, and reading the fine print twice — every single package.",
    },
  ];

  const values = [
    {
      icon: Heart,
      title: "Safe & Genuine",
      description: "Clients consistently describe my energy as safe, warm, and trustworthy. Notary work is intimate — you're signing things that change your life. I take that seriously.",
    },
    {
      icon: Handshake,
      title: "Above & Beyond",
      description: "Late-night hospital visits. Last-minute apostilles before international flights. If you need it done right and done now, I'm the call you make.",
    },
    {
      icon: Star,
      title: "Nashville to the Core",
      description: "Born and raised here. I know our courthouses, our hospitals, our title companies, and our neighborhoods. Local knowledge is professional capital.",
    },
  ];

  return (
    <main data-testid="about-page">
      {/* Hero */}
      <section className="bg-[#800000] py-32 pt-40 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase">Meet The Owner</span>
          </div>
          <h1 className="font-serif-display text-5xl sm:text-6xl font-semibold text-white mb-5">
            India.
            <span className="block italic text-3xl sm:text-4xl text-white/85 mt-2 font-light">
              MBA. Nashville Native. Notary by Calling.
            </span>
          </h1>
          <p className="text-lg text-white/85 max-w-2xl leading-relaxed">
            From 17 years leading ministry programs for one of Nashville's largest congregations to becoming the trusted partner notary for leading law firms, title companies, and national homebuilders — this is the story behind India's Inking Service.
          </p>
        </div>
      </section>

      {/* Main about */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            {/* Image */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative max-w-md mx-auto">
                <div className="absolute -inset-4 border-2 border-[#D4AF37]/40 rounded-sm transform rotate-2" />
                <div className="absolute -inset-8 border border-[#D4AF37]/20 rounded-sm transform -rotate-1" />
                <div className="relative bg-[#D4AF37] p-1.5 rounded-sm shadow-2xl">
                  <img
                    src={BRAND.headshotUrl}
                    alt="India — Owner of India's Inking Service, Nashville TN"
                    className="w-full aspect-[3/4] object-cover rounded-sm"
                  />
                </div>
                {/* Stars badge */}
                <div className="absolute -bottom-6 -right-6 bg-white px-5 py-4 rounded-sm shadow-2xl border-l-4 border-[#D4AF37] hidden sm:block">
                  <div className="flex items-center gap-1 mb-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} weight="fill" size={16} className="text-[#D4AF37]" />
                    ))}
                  </div>
                  <p className="text-xs text-[#5C5C5C] tracking-wide">5.0 · Verified Google</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 text-[#800000] px-4 py-2 rounded-sm mb-6">
                <MapPinLine weight="fill" size={16} />
                <span className="text-xs font-semibold tracking-[0.2em] uppercase">Nashville Native</span>
              </div>

              <h2 className="font-serif-display text-4xl sm:text-5xl font-semibold text-[#800000] mb-6 leading-tight">
                Why I started <span className="italic">India's Inking Service.</span>
              </h2>

              <div className="space-y-5 text-[#1A1A1A] leading-relaxed text-base">
                <p>
                  Before notary work became my full-time calling, I spent <strong className="text-[#800000]">17 years as Program Director</strong> managing children and youth ministries at one of Nashville's most prominent congregations — a <strong className="text-[#800000]">30,000-member</strong> church family. My role centered on program development, family-experience design, and the day-to-day coordination of large-scale assets across multiple campuses.
                </p>
                <p>
                  Pair that with an <strong className="text-[#800000]">MBA</strong> and years partnering as a trusted notary on high-volume residential closings with Pulte and other top-tier national homebuilders, and you get the foundation behind India's Inking Service: heart-of-service hospitality, paired with corporate-grade documentation discipline.
                </p>
                <p>
                  Whether you're an executive needing an apostille before a flight, a daughter sitting at her father's hospital bedside with a power of attorney, or a couple eloping at the courthouse on a Tuesday afternoon — <strong className="text-[#800000]">I show up. On time. Prepared. With heart.</strong>
                </p>
              </div>

              {/* Pull quote */}
              <div className="mt-10 pl-6 border-l-4 border-[#D4AF37]">
                <Quotes weight="fill" size={28} className="text-[#D4AF37] mb-3" />
                <p className="font-serif-display text-2xl italic text-[#800000] leading-relaxed">
                  "Her energy felt SAFE & Genuine!"
                </p>
                <p className="text-sm text-[#5C5C5C] mt-2 tracking-wide">— Kenyata Hillsman · Google Review</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-24 bg-[#FBF8F1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">Credentials</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-semibold text-[#800000] mb-4">
              Corporate background. <span className="italic">Local heart.</span>
            </h2>
            <p className="text-[#5C5C5C] text-base sm:text-lg">
              The professional foundation behind every signature.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {credentials.map((cred, idx) => (
              <div
                key={idx}
                data-testid={`credential-card-${idx}`}
                className="bg-white border border-[#E8E2D5] rounded-sm p-8 hover:border-[#D4AF37]/50 hover:shadow-xl transition-all"
              >
                <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 flex items-center justify-center mb-6">
                  <cred.icon weight="duotone" size={28} className="text-[#D4AF37]" />
                </div>
                <h3 className="font-serif-display text-xl font-semibold text-[#800000] mb-3 leading-tight">
                  {cred.title}
                </h3>
                <p className="text-[#5C5C5C] text-sm leading-relaxed">
                  {cred.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2B Trust callout */}
      <section data-testid="b2b-trust-callout" className="py-16 bg-[#800000] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="bg-white/[0.04] backdrop-blur-sm border border-[#D4AF37]/30 rounded-sm p-10 sm:p-14 text-center">
            <Scales weight="duotone" size={40} className="text-[#D4AF37] mx-auto mb-5" />
            <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-5">
              B2B · Law Firms & Title Companies
            </p>
            <p className="font-serif-display text-3xl sm:text-4xl lg:text-[2.7rem] font-semibold text-white leading-[1.2] max-w-3xl mx-auto">
              "The preferred, first-call mobile notary <br className="hidden sm:block" />
              for <span className="italic gold-shimmer">leading local law firms and title companies.</span>"
            </p>
            <div className="mt-8 inline-flex items-center gap-3">
              <div className="h-px w-10 bg-[#D4AF37]" />
              <span className="text-white/70 text-[11px] tracking-[0.25em] uppercase">
                Trusted across Middle Tennessee
              </span>
              <div className="h-px w-10 bg-[#D4AF37]" />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">What Clients Say</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-semibold text-[#800000] mb-4">
              The values clients <span className="italic">feel</span>.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                data-testid={`value-card-${index}`}
                className="bg-[#FBF8F1] rounded-sm border border-[#E8E2D5] p-8 hover:shadow-xl transition-all duration-500"
              >
                <div className="w-14 h-14 rounded-full bg-[#800000]/5 flex items-center justify-center mb-6">
                  <value.icon weight="duotone" size={28} className="text-[#800000]" />
                </div>
                <h3 className="font-serif-display text-xl font-semibold text-[#800000] mb-3">
                  {value.title}
                </h3>
                <p className="text-[#5C5C5C] text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#800000]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif-display text-4xl sm:text-5xl font-semibold text-white mb-5">
            Ready to work with India?
          </h2>
          <p className="text-lg text-white/85 mb-10">
            Let's get your documents handled the right way.
          </p>
          <Link
            to="/booking"
            data-testid="about-book-now-btn"
            className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#F5E6A8] transition-all px-10 py-4 rounded-sm font-semibold text-sm tracking-[0.15em] uppercase shadow-2xl"
          >
            <CalendarCheck weight="bold" size={22} />
            Book Your Appointment
          </Link>
        </div>
      </section>
    </main>
  );
};
